import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileSpreadsheet, Loader2, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { api, downloadBlob, errMsg } from "../lib/api";

const POLL_MS = 2000;
const fmtN = (n) => Number(n || 0).toLocaleString("en-IN");

/**
 * Phase 140 — "Export Excel" for the Properties register. The export is a background job (registers run to
 * 100k+ rows): click → job queued → this component polls until it is ready → "Download" appears.
 * `filters` are the APPLIED filters (same ones the list and the summary cards use).
 */
export function PropertyExportButton({ code, filters, disabled = false }) {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const timer = useRef(null);

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  useEffect(() => stop, []);

  const poll = useCallback((jobId) => {
    stop();
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/t/${code}/properties/export/${jobId}`);
        setJob(r.data);
        if (r.data.status === "done" || r.data.status === "failed" || r.data.status === "expired") {
          stop();
          if (r.data.status === "done") toast.success(`Export ready — ${fmtN(r.data.rows)} properties`, { description: r.data.filename });
          if (r.data.status === "failed") toast.error("Export failed", { description: r.data.error || "Please try again" });
        }
      } catch (e) {
        stop();
        setJob((j) => (j ? { ...j, status: "failed", error: errMsg(e) } : j));
      }
    }, POLL_MS);
  }, [code]);

  const start = async () => {
    setStarting(true);
    try {
      const body = {};
      Object.entries(filters || {}).forEach(([k, v]) => { if (v) body[k] = v; });
      const r = await api.post(`/t/${code}/properties/export`, body);
      setJob(r.data);
      toast.info("Preparing your Excel export…", { description: "You can keep working — a Download button appears here when it is ready." });
      poll(r.data.id);
    } catch (e) {
      toast.error("Could not start the export", { description: errMsg(e) });
    } finally {
      setStarting(false);
    }
  };

  const download = async () => {
    if (!job) return;
    setDownloading(true);
    try {
      await downloadBlob(`/t/${code}/properties/export/${job.id}/download`, job.filename);
    } catch (e) {
      toast.error("Download failed", { description: errMsg(e) });
    } finally {
      setDownloading(false);
    }
  };

  const busy = job && (job.status === "queued" || job.status === "running");

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="properties-export">
      <Button type="button" variant="outline" size="sm" className="h-10" onClick={start} disabled={disabled || starting || !!busy}
        data-testid="properties-export-button" title="Export the filtered properties to Excel (prepared in the background)">
        {starting || busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}
        {busy ? `Preparing… ${job.rows ? fmtN(job.rows) + " rows" : ""}` : "Export Excel"}
      </Button>
      {job && job.status === "done" && (
        <div className="flex items-center gap-1.5" data-testid="properties-export-ready">
          <Button type="button" size="sm" className="h-10" onClick={download} disabled={downloading} data-testid="properties-export-download">
            {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            Download ({fmtN(job.rows)} rows)
          </Button>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => setJob(null)} aria-label="Dismiss export" data-testid="properties-export-dismiss">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {job && job.status === "failed" && (
        <Badge variant="outline" className="h-8 gap-1 text-destructive border-destructive/30 bg-destructive/5 font-normal" data-testid="properties-export-failed">
          <AlertCircle className="h-3.5 w-3.5" /> {job.error || "Export failed"}
          <button type="button" className="ml-1 underline" onClick={() => setJob(null)} data-testid="properties-export-failed-dismiss">dismiss</button>
        </Badge>
      )}
    </div>
  );
}
